import { useEffect, useState, type ReactNode } from 'react';
import { Search, ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, ExternalLink } from 'lucide-react';
import { getApiBase } from '../lib/apiClient';
import { fmtTs } from '../lib/globalTz';

type Verdict = 'malicious' | 'suspicious' | 'clean' | 'unknown';
type IocType = 'ip' | 'domain' | 'url' | 'hash' | 'unknown';

interface ProviderResult {
  provider: 'virustotal' | 'abuseipdb' | 'otx' | 'spur';
  ok: boolean;
  verdict: Verdict;
  summary?: string;
  details?: Record<string, unknown>;
  link?: string;
  error?: string;
}

interface IocReport {
  ioc: string;
  type: IocType;
  cachedAt: string;
  fromCache: boolean;
  overallVerdict: Verdict;
  malicious: number;
  suspicious: number;
  total: number;
  results: ProviderResult[];
}

interface ProviderInfo {
  name: string;
  configured: boolean;
  supports: string[];
}

const PROVIDER_LABEL: Record<ProviderResult['provider'], string> = {
  virustotal: 'VirusTotal',
  abuseipdb: 'AbuseIPDB',
  otx: 'OTX AlienVault',
  spur: 'Spur',
};

function verdictStyle(v: Verdict): { cls: string; icon: ReactNode } {
  switch (v) {
    case 'malicious':
      return {
        cls: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300 border-red-500',
        icon: <ShieldAlert size={14} className="text-red-600 dark:text-red-400" />,
      };
    case 'suspicious':
      return {
        cls: 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300 border-orange-500',
        icon: <ShieldAlert size={14} className="text-orange-600 dark:text-orange-400" />,
      };
    case 'clean':
      return {
        cls: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-500',
        icon: <ShieldCheck size={14} className="text-green-600 dark:text-green-400" />,
      };
    default:
      return {
        cls: 'bg-muted text-muted-foreground border-muted',
        icon: <ShieldQuestion size={14} className="text-muted-foreground" />,
      };
  }
}

interface IocReputationCardProps {
  /** Customer scope. When omitted (e.g. MSSP "default" workspace), the lookup
   *  button is disabled because the backend now requires a customerId to gate
   *  /ioc/reputation against the threat_intel tab matrix. */
  customerId?: string;
}

export function IocReputationCard({ customerId }: IocReputationCardProps = {}) {
  const [ioc, setIoc] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<IocReport | null>(null);
  const [error, setError] = useState('');
  const [providers, setProviders] = useState<Record<string, ProviderInfo>>({});

  useEffect(() => {
    fetch(`${getApiBase()}/ioc/reputation/providers`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setProviders(d.providers ?? {}))
      .catch(() => {});
  }, []);

  async function lookup() {
    const trimmed = ioc.trim();
    if (!trimmed) return;
    if (!customerId) {
      setError('Select a customer to run reputation lookups.');
      return;
    }
    setLoading(true);
    setError('');
    setReport(null);
    try {
      const resp = await fetch(`${getApiBase()}/ioc/reputation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ioc: trimmed, customerId }),
      });
      const data = await resp.json() as { report?: IocReport; error?: string; code?: string };
      if (!resp.ok || data.error) {
        setError(data.error ?? `Lookup failed (${resp.status})`);
      } else if (data.report) {
        setReport(data.report);
      }
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setLoading(false);
    }
  }

  const overall = report ? verdictStyle(report.overallVerdict) : null;
  const configuredCount = Object.values(providers).filter(p => p.configured).length;

  return (
    <section className="bg-card rounded-xl p-5 shadow mb-6">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Search size={16} className="text-primary" />
            IOC Reputation Lookup
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Enrich an IP, domain, URL, or file hash across configured threat-intel providers.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(providers).map(([key, info]) => (
            <span
              key={key}
              className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                info.configured
                  ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300'
                  : 'bg-muted text-muted-foreground'
              }`}
              title={info.configured ? `${info.name} configured (${info.supports.join(', ')})` : `${info.name} not configured`}
            >
              {info.name}
            </span>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={ioc}
          onChange={e => setIoc(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void lookup(); }}
          placeholder="e.g. 8.8.8.8, evil.com, https://example.com/path, or SHA256 hash"
          className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
          disabled={loading}
        />
        <button
          onClick={() => void lookup()}
          disabled={loading || !ioc.trim() || configuredCount === 0}
          className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center gap-2"
          title={configuredCount === 0 ? 'Configure at least one provider in Settings → Threat Intel' : 'Lookup IOC'}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {loading ? 'Looking up…' : 'Enrich'}
        </button>
      </div>

      {configuredCount === 0 && (
        <p className="text-xs text-orange-600 dark:text-orange-400 mb-3">
          No providers configured. Add API keys in Settings → Threat Intel.
        </p>
      )}

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 p-3 rounded-lg mb-3">
          {error}
        </div>
      )}

      {report && overall && (
        <div className="space-y-3">
          <div className={`flex items-center justify-between gap-3 p-3 rounded-lg border-l-4 ${overall.cls}`}>
            <div className="flex items-center gap-2 min-w-0">
              {overall.icon}
              <div className="min-w-0">
                <div className="text-xs font-bold uppercase tracking-wide truncate">
                  {report.overallVerdict} · {report.type}
                </div>
                <div className="text-xs text-muted-foreground truncate" title={report.ioc}>
                  {report.ioc}
                </div>
              </div>
            </div>
            <div className="text-right text-[11px] text-muted-foreground whitespace-nowrap">
              <div>
                {report.malicious + report.suspicious}/{report.total} flagged
                {' '}({report.malicious} malicious, {report.suspicious} suspicious)
              </div>
              <div>{report.fromCache ? 'cached' : 'live'} · {fmtTs(report.cachedAt, { timeStyle: 'short' })}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {report.results.map(r => {
              const v = verdictStyle(r.verdict);
              return (
                <div key={r.provider} className={`p-3 rounded-lg border-l-4 ${v.cls} bg-card`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {v.icon}
                      <span className="text-xs font-bold text-foreground">{PROVIDER_LABEL[r.provider]}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase">{r.verdict}</span>
                      {r.link && (
                        <a href={r.link} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary" title="Open provider details">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                  {r.summary && (
                    <div className="text-[11px] text-foreground mt-1 break-words">{r.summary}</div>
                  )}
                  {r.error && r.error !== 'no_key' && r.error !== 'unsupported_type' && (
                    <div className="text-[11px] text-red-600 dark:text-red-400 mt-1 break-words">{r.error}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
